import { useState, useRef, useImperativeHandle, forwardRef, useEffect, useMemo, useCallback } from "react";
import { Table, Radio } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as XLSX from "xlsx";
import ServiceExcelClient from "./service-excel-client";
import "./service-excel.css";

dayjs.extend(customParseFormat);

const ServiceExcelClients = forwardRef((props, ref) => {
  const { onClientsChange } = props || {};
  const [clients, setClients] = useState([]);
  const [validationErrors, setValidationErrors] = useState({}); // { "recordKey-fieldName": "error" | "warning" }
  const [editingRecord, setEditingRecord] = useState(null); // Запись, которая редактируется
  const [filterType, setFilterType] = useState("all"); // "all" | "errors" | "warnings"
  const clientsRef = useRef(null);

  // Маппинг колонок Excel на поля таблиц
  const clientsColumnMap = {
    "Внешний ID": "externalId",
    "ФИО": "fio",
    "Телефон": "phone",
    "EMAIL": "email",
    "Дата рождения": "birthDate",
    "Адрес": "address",
    "Пол": "gender",
    "№ карты": "cardNumber",
    "Комментарий": "comment",
    "Депозит": "deposit",
    "Паспорт": "passport",
    "Дата регистрации": "registrationDate"
  };
  // console.log(clients)
  // Валидация для таблицы Клиенты
  const validateClientField = (fieldName, value, record, allClients) => {
    const isEmpty = value === null || value === undefined || value === "";
    if (!isEmpty) {
      const numericValue = typeof value === "string"
        ? parseFloat(value)
        : value;
      if (["fio", "phone", "email", "address", "cardNumber", "comment", "passport"].includes(fieldName) && !["string", "number", "boolean"].includes(typeof value)) return "error";
      if (["gender", "deposit"].includes(fieldName) &&
          (typeof numericValue !== "number" || !Number.isFinite(numericValue))) return "error";
      if (fieldName === "gender" && !Number.isInteger(numericValue)) return "error";
    }

    const isValidIsoDate = (value) => {
      if (value === null || value === undefined || value === "") return true;
      if (typeof value !== "string" && !(value instanceof Date)) return false;
      const d = typeof value === "string" && /^\d{2}\.\d{2}\.\d{4}$/.test(value)
        ? dayjs(value, "DD.MM.YYYY", true)
        : dayjs(value);
      return d.isValid();
    };

    switch (fieldName) {
      case "externalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка на дубли
        const duplicateExternalId = allClients.filter(
          (c) => c.key !== record.key && c.externalId === value
        );
        if (duplicateExternalId.length > 0) return "error"; // Критическая ошибка
        return null;

      case "fio":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка на дубли (предупреждение)
        const duplicateFio = allClients.filter(
          (c) => c.key !== record.key && c.fio === value
        );
        if (duplicateFio.length > 0) return "warning"; // Предупреждение
        return null;

      case "phone":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка формата +7XXXXXXXXXX
        const phoneRegex = /^\+7\d{10}$/;
        if (!phoneRegex.test(value)) return "error"; // Критическая ошибка
        // Проверка на дубли
        const duplicatePhone = allClients.filter(
          (c) => c.key !== record.key && c.phone === value
        );
        if (duplicatePhone.length > 0) return "error"; // Критическая ошибка
        return null;

      case "email":
        // if (isEmpty) return "error"; // Критическая ошибка
        // Проверка формата email
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) return "error"; // Критическая ошибка
        // Проверка на дубли
        const duplicateEmail = allClients.filter(
          (c) => c.key !== record.key && c.email === value
        );
        if (duplicateEmail.length > 0) return "warning"; // Предупреждение
        return null;

      case "birthDate":
        if (isEmpty) return "warning"; // Предупреждение
        if (!isValidIsoDate(value)) return "error";
        return null;

      case "address":
        if (isEmpty) return "warning"; // Предупреждение
        return null;

      case "gender":
        if (isEmpty) return "warning"; // Предупреждение
        return null;

      case "cardNumber": {
        const normalizedCard = String(value ?? "").trim().toLowerCase();
        if (!normalizedCard) return "warning";
        const duplicateCard = allClients.some((client) =>
          client.key !== record.key &&
          String(client.cardNumber ?? "").trim().toLowerCase() === normalizedCard
        );
        return duplicateCard ? "error" : null;
      }

      case "deposit":
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "passport":
        if (isEmpty) return "warning"; // Предупреждение
        if (!/^\s*(?:\d\s*){10}$/.test(String(value))) return "error";
        return null;
      
      case "registrationDate":
        if (isEmpty) return "warning";
        if (!isValidIsoDate(value)) return "error";
        return null;

      default:
        return null;
    }
  };

  // Функция для валидации всех полей записи
  const validateRecord = (record, allClients) => {
    const errors = {};
    const fieldsToValidate = ["externalId", "fio", "phone", "email", "birthDate", "address", "gender", "cardNumber", "comment", "deposit", "passport", "registrationDate"];
    
    fieldsToValidate.forEach(fieldName => {
      const validation = validateClientField(fieldName, record[fieldName], record, allClients);
      if (validation) {
        errors[`${record.key}-${fieldName}`] = validation;
      }
    });
    
    return errors;
  };

  const getCellClassName = useCallback((fieldName, record) => {
    const errorKey = `${record.key}-${fieldName}`;
    const validation = validationErrors[errorKey];
    
    let className = "service-excel-cell";
    if (validation === "error") {
      className += " service-excel-cell-error";
    } else if (validation === "warning") {
      className += " service-excel-cell-warning";
    }
    return className;
  }, [validationErrors]);

  const handleRowClick = useCallback((record) => {
    const editable = { ...record };
    // Если дата невалидная - при редактировании показываем пустое значение
    ["birthDate", "registrationDate"].forEach((field) => {
      const val = editable[field];
      if (val !== null && val !== undefined && val !== "" && !dayjs(val).isValid()) {
        editable[field] = null; 
      }
    });
    setEditingRecord(editable);
  }, []);

  const handleSave = (updatedRecord) => {
    let phone = String(updatedRecord.phone ?? "").trim().replace(/[\s()-]/g, "");
    if (/^\d{10}$/.test(phone)) phone = `+7${phone}`;
    else if (/^[78]\d{10}$/.test(phone)) phone = `+7${phone.slice(1)}`;
    updatedRecord = { ...updatedRecord, phone };
    setClients(prev => {
      // Проверяем, существует ли запись в массиве
      const existingIndex = prev.findIndex(item => item.key === updatedRecord.key);
      let newClients;
      
      if (existingIndex >= 0) {
        // Обновляем существующую запись
        newClients = prev.map(item => 
          item.key === updatedRecord.key ? updatedRecord : item
        );
      } else {
        // Добавляем новую запись
        newClients = [...prev, updatedRecord];
      }
      
      // Валидация после сохранения
      const recordErrors = validateRecord(updatedRecord, newClients);
      
      setValidationErrors(validationErrorsPrev => {
        const filtered = Object.keys(validationErrorsPrev)
          .filter(key => !key.startsWith(`${updatedRecord.key}-`))
          .reduce((obj, key) => {
            obj[key] = validationErrorsPrev[key];
            return obj;
          }, {});
        Object.assign(filtered, recordErrors);
        return filtered;
      });
      
      if (onClientsChange) onClientsChange(newClients.length);
      return newClients;
    });
    
    setEditingRecord(null);
  };

  const handleCancel = () => {
    setEditingRecord(null);
  };


  const addRow = () => {
    const newKey = Date.now();
    const newRecord = {
      key: newKey,
      externalId: "",
      fio: "",
      phone: "",
      email: "",
      birthDate: null,
      address: "",
      gender: null,
      cardNumber: "",
      comment: "",
      deposit: null,
      passport: ""
    };
    setEditingRecord(newRecord);
  };

  const deleteRow = useCallback((record) => {
    setClients(prev => {
      const newClients = prev.filter(item => item.key !== record.key);
      if (onClientsChange) onClientsChange(newClients.length);
      return newClients;
    });
    // Удаляем все ключи валидации для удаленной записи
    setValidationErrors(prev => {
      const filtered = Object.keys(prev)
        .filter(key => !key.startsWith(`${record.key}-`))
        .reduce((obj, key) => {
          obj[key] = prev[key];
          return obj;
        }, {});
      return filtered;
    });
  }, [onClientsChange]);

  const clearClients = () => {
    setClients([]);
    setValidationErrors({});
    setEditingRecord(null);
    if (onClientsChange) onClientsChange(0);
  };

  const parseSheetData = (worksheet) => {
    if (!worksheet) return [];
    
    // Используем raw: false для правильной обработки дат
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1, 
      defval: "",
      raw: true,
      dateNF: "dd.mm.yyyy"
    });
    if (jsonData.length < 2) return []; // Нет заголовков или данных

    const headers = jsonData[0].map(h => String(h || "").trim());
    const rows = jsonData.slice(1);

    // Определяем поля, которые должны быть датами
    const dateFields = ["birthDate", "registrationDate"];

    return rows.map((row, index) => {
      // Проверяем первую колонку - если пустая, пропускаем запись
      const firstColumnValue = row[0];
      if (firstColumnValue === "" || firstColumnValue === null || firstColumnValue === undefined) {
        return null; // Возвращаем null для фильтрации
      }
      
      const record = { key: `client-${Date.now()}-${index}-${Math.random()}` };
      headers.forEach((header, colIndex) => {
        // Ищем поле с учетом возможных различий в регистре и пробелах
        let fieldName = clientsColumnMap[header];
        if (!fieldName) {
          const matchedKey = Object.keys(clientsColumnMap).find(key => 
            key.toLowerCase().trim() === header.toLowerCase().trim()
          );
          if (matchedKey) {
            fieldName = clientsColumnMap[matchedKey];
          }
        }
        
        if (fieldName) {
          let value = row[colIndex];
          
          // Обработка пустых значений
          if (value === "" || value === null || value === undefined) {
            value = null;
          } 
          // Обработка дат
          else if (dateFields.includes(fieldName)) {
            if (typeof value === "number") {
              const excelEpoch = new Date(1899, 11, 30);
              const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
              value = date.toISOString();
            } else if (typeof value === "string") {
              const trimmedValue = value.trim();

              // Строгий формат DD.MM.YYYY
              const parsed = dayjs(trimmedValue, "DD.MM.YYYY", true);

              if (parsed.isValid()) {
                value = parsed.format("DD.MM.YYYY");
              } else {
                value = trimmedValue;
              }
            }
          }
          // Обработка телефона
          else if (fieldName === "phone") {
            const phone = String(value).trim().replace(/[\s()-]/g, "");
            if (/^\d{10}$/.test(phone)) value = `+7${phone}`;
            else if (/^[78]\d{10}$/.test(phone)) value = `+7${phone.slice(1)}`;
            else value = phone;
          }
          // Обработка чисел
          else if (fieldName === "deposit" || fieldName === "gender") {
            if (typeof value === "string") {
              const numValue = parseFloat(value);
              value = isNaN(numValue) ? value : numValue;
            }
          } else if (fieldName === "comment" && typeof value === "number") {
            value = String(value);
          } else if (fieldName === "cardNumber") {
            if (value !== null && value !== undefined && value !== "") {
              value = String(value).trim();
            } else {
              value = null;
            }
          }
          
          record[fieldName] = value;
        }
      });
      
      return record;
    }).filter(record => {
      // Фильтруем null записи (где первая колонка была пустая)
      if (record === null) return false;
      // Фильтруем пустые строки - проверяем, есть ли хотя бы одно непустое значение
      const values = Object.values(record).filter(key => key !== "key");
      return values.some(value => value !== null && value !== "" && value !== undefined);
    });
  };

  const loadFromExcel = (worksheet) => {
    const clientsData = parseSheetData(worksheet);
    setClients(clientsData);
    setEditingRecord(null);
    return clientsData.length;
  };

  useEffect(() => {
    if (!clients.length) {
      setValidationErrors({});
      return;
    }
    const errors = {};
    clients.forEach((record) => {
      Object.assign(errors, validateRecord(record, clients));
    });
    setValidationErrors(errors);
  }, [clients]);

  // Синхронизация количества клиентов с родительским компонентом
  useEffect(() => {
    if (onClientsChange) {
      onClientsChange(clients.length);
    }
  }, [clients, onClientsChange]);

  // Проверка наличия критических ошибок
  const hasCriticalErrors = () => {
    console.log("hasCriticalErrors")
    return clients.some(record =>
      Object.values(validateRecord(record, clients)).includes("error")
    );
  };

  // Получение данных для сохранения
  const getClientsData = () => {
    const parseFloat = (value) => {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : 0;
    };
    const parseInt = (value, defaultValue = 0) => {
      const number = Number.parseInt(value, 10);
      return Number.isFinite(number) ? number : defaultValue;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      const date = typeof dateStr === "string" && /^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)
        ? dayjs(dateStr, "DD.MM.YYYY", true)
        : dayjs(dateStr);
      return date.isValid() ? date.format("YYYY-MM-DD") : null;
    };

    return clients.map(client => ({
      Id: parseInt(client.externalId),
      Name: String(client.fio ?? ""),
      Phone: String(client.phone ?? ""),
      Email: String(client.email ?? ""),
      DateBirth: formatDate(client.birthDate),
      Address: String(client.address ?? ""),
      Sex: parseInt(client.gender, null),
      Card: String(client.cardNumber ?? ""),
      Comment: String(client.comment ?? ""),
      Depos: parseFloat(client.deposit),
      Passport: String(client.passport ?? ""),
      DateReg: client.registrationDate ? formatDate(client.registrationDate) : null,
      CompanyId: 0
    }));
  };

  // Проверка наличия ошибок у записи
  const hasRecordError = useCallback((record) => {
    const fieldsToValidate = ["externalId", "fio", "phone", "email", "birthDate", "address", "gender", "cardNumber", "comment", "deposit", "passport", "registrationDate"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "error") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Проверка наличия предупреждений у записи
  const hasRecordWarning = useCallback((record) => {
    const fieldsToValidate = ["externalId", "fio", "phone", "email", "birthDate", "address", "gender", "cardNumber", "comment", "deposit", "passport", "registrationDate"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "warning") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Подсчет записей с ошибками и предупреждениями
  const getValidationCounts = () => {
    const errorKeys = new Set();
    const warningKeys = new Set();
    
    // Получаем множество существующих ключей записей (как строки для сравнения)
    const existingKeys = new Set(clients.map(c => String(c.key)));
    
    // Получаем список всех полей для валидации
    const fieldsToValidate = ["externalId", "fio", "phone", "email", "birthDate", "address", "gender", "cardNumber", "comment", "deposit", "passport", "registrationDate"];
    
    // Сначала собираем все ошибки и предупреждения по записям
    Object.entries(validationErrors).forEach(([key, type]) => {
      // Извлекаем ключ записи - находим последнее поле из списка полей в ключе
      let recordKey = null;
      for (const field of fieldsToValidate) {
        if (key.endsWith(`-${field}`)) {
          recordKey = key.slice(0, -(field.length + 1)); // Убираем "-fieldName" с конца
          break;
        }
      }
      
      // Если не нашли через поля, используем старый метод как fallback
      if (!recordKey) {
        const parts = key.split('-');
        if (parts.length > 1) {
          // Берем все части кроме последней (которая является fieldName)
          recordKey = parts.slice(0, -1).join('-');
        } else {
          recordKey = key;
        }
      }
      
      // Учитываем только ошибки для существующих записей
      if (recordKey && existingKeys.has(recordKey)) {
        if (type === "error") {
          errorKeys.add(recordKey);
        } else if (type === "warning") {
          warningKeys.add(recordKey);
        }
      }
    });
    
    return {
      errors: errorKeys.size,
      warnings: warningKeys.size
    };
  };

  // Предоставляем методы через ref
  useImperativeHandle(ref, () => ({
    clearClients,
    loadFromExcel,
    hasCriticalErrors,
    getClientsData,
    getClients: () => clients,
    addRow,
    getValidationCounts
  }));

  const getClientsColumns = useMemo(() => [
    {
      title: "",
      width: 60,
      align: "center",
      render: (_, record) => (
        <img
          src="/images/trashcan.svg"
          alt="delete"
          onClick={(e) => {
            e.stopPropagation();
            deleteRow(record);
          }}
          className="img-edit-btn service-excel-action-icon"
        />
      ),
    },
    {
      title: "Внешний ID",
      dataIndex: "externalId",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("externalId", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "ФИО",
      dataIndex: "fio",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("fio", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("phone", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "EMAIL",
      dataIndex: "email",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("email", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Дата рождения",
      dataIndex: "birthDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("birthDate", record);
        let displayValue = "";
        if (record.birthDate) {
          const parsed = dayjs(record.birthDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          } else {
            displayValue = String(record.birthDate); // Показываем как есть, что есть ошибка!
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Адрес",
      dataIndex: "address",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("address", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Пол",
      dataIndex: "gender",
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      render: (text, record) => {
        const className = getCellClassName("gender", record) + " service-excel-cell-center";
        const genderText = text === 0 ? "Ж" : text === 1 ? "М" : "";
        return <div className={className}>{genderText}</div>;
      },
    },
    {
      title: "№ карты",
      dataIndex: "cardNumber",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("cardNumber", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Комментарий",
      dataIndex: "comment",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("comment", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Депозит",
      dataIndex: "deposit",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("deposit", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Паспорт",
      dataIndex: "passport",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("passport", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Дата регистрации",
      dataIndex: "registrationDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("registrationDate", record);
        let displayValue = "";
        if (record.registrationDate) {
          const parsed = dayjs(record.registrationDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          } else {
            displayValue = String(record.registrationDate); // Показываем как есть, что есть ошибка!
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
  ], [deleteRow, getCellClassName]);

  const getTableHeight = (ref) => {
    if (!ref.current) return 400;
    const headerSize = ref.current.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
    return ref.current ? (document.documentElement.offsetHeight - headerSize - ref.current.getBoundingClientRect().top - window.scrollY - 150) : 400;
  };

  // Компоненты для таблиц с убранным padding в body ячейках
  const tableComponents = {
    body: {
      cell: (props) => {
        const { children, ...restProps } = props;
        return (
          <td {...restProps} style={{ ...restProps.style, padding: 0 }}>
            {children}
          </td>
        );
      },
    },
  };

  // Фильтрация данных в зависимости от выбранного фильтра
  const filteredClients = useMemo(() => {
    if (filterType === "all") {
      return clients;
    } else if (filterType === "errors") {
      return clients.filter(client => hasRecordError(client));
    } else if (filterType === "warnings") {
      return clients.filter(client => hasRecordWarning(client));
    }
    return clients;
  }, [clients, filterType, hasRecordError, hasRecordWarning]);

  // Получаем счетчики для отображения
  const validationCounts = getValidationCounts();

  return (
    <>
      <div ref={clientsRef}>
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <Radio.Group 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Всего ({clients.length})
            </Radio.Button>
            <Radio.Button value="errors">
              Ошибок ({validationCounts.errors})
            </Radio.Button>
            <Radio.Button value="warnings">
              Предупреждений ({validationCounts.warnings})
            </Radio.Button>
          </Radio.Group>
        </div>
        <Table
          dataSource={filteredClients}
          columns={getClientsColumns}
          rowKey="key"
          size="small"
          pagination={false}
          scroll={{ x: "max-content", y: getTableHeight(clientsRef) }}
          bordered
          components={tableComponents}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" }
          })}
        />
      </div>
      {editingRecord && (
        <ServiceExcelClient
          record={editingRecord}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </>
  );
});

ServiceExcelClients.displayName = "ServiceExcelClients";

export default ServiceExcelClients;

